import { z } from 'zod';
import { base44GroupKey, base44ScraperType } from './base44-templates';
import {
  computeQuoteCalculation,
  deriveFormulaVariables,
  type ScraperType,
} from './quote-calculation-engine';
import {
  formulasForScraperType,
  QUOTE_CALC_GROUP_DEFAULTS,
  QUOTE_CALC_GROUP_KEYS,
  toQuoteCalcInput,
  type QuoteCalcFormulaRow,
  type QuoteCalcGroup,
  type QuoteCalcGroupKey,
  type QuoteCalcLine,
  type QuoteCalcStatus,
} from './quote-calculation.schema';

/**
 * Übernahme der Kalkulationen aus der base44-App „Vertrieb" (ADR 0112):
 * Exporte `Kalkulation`, `Kalkulationsposition`, `Artikel`, `Angebot`.
 *
 * Grundsatz: Nach dem Import rechnet die Kalkulation genau, was die alte App
 * gespeichert hat. Wo die Formel eine andere Menge ergäbe als gespeichert, oder
 * die alte App die Menge gesperrt hatte, wird die Menge „von Hand" (Schloss);
 * weicht der Preis vom Artikel ab, ebenso. Alles andere rechnet wieder live.
 */

const num = z.number().finite().nullish();
const str = z.string().nullish();

export const base44CalculationRecordSchema = z.object({
  id: z.string().min(1),
  kalkulation_id: z.string().trim().min(1),
  projektname: str,
  angebotsuntertitel: str,
  datum: str,
  created_date: str,
  type: str,
  kunde: str,
  beckenanzahl: num,
  laenge: num,
  breite: num,
  hoehe: num,
  wasserspiegel: num,
  balkenabstand: num,
  raeumgeschwindigkeit: num,
  anzahl_balken: num,
  balkenlaenge: num,
  materialgemeinkosten_prozent: num,
  vetriebsgemeinkosten_prozent: num,
  verkaeufer_provision_prozent: num,
  finaler_verkaufspreis: num,
  kunststoff_prozent: num,
  elektro_prozent: num,
  stahl_prozent: num,
  montage_prozent: num,
  doku_prozent: num,
  fracht_prozent: num,
  optionen_prozent: num,
  kunststoff_calc_mode: str,
  elektro_calc_mode: str,
  stahl_calc_mode: str,
  montage_calc_mode: str,
  doku_calc_mode: str,
  fracht_calc_mode: str,
  optionen_calc_mode: str,
  doku_kosten_aufteilen: z.boolean().nullish(),
  fracht_kosten_aufteilen: z.boolean().nullish(),
});
export type Base44CalculationRecord = z.infer<typeof base44CalculationRecordSchema>;

export const base44PositionRecordSchema = z.object({
  id: z.string().min(1).max(64),
  kalkulation_id: z.string().min(1),
  gruppe: z.string(),
  kategorie: z.string(),
  artikel_id: str,
  menge: num,
  einzel_ek: num,
  einzel_vk: num,
  menge_locked: z.boolean().nullish(),
  vk_angebot_override: num,
  show_in_offer: z.boolean().nullish(),
  show_in_specs: z.boolean().nullish(),
  reihenfolge: num,
});
export type Base44PositionRecord = z.infer<typeof base44PositionRecordSchema>;

export const base44ArticleRecordSchema = z.object({
  id: z.string().min(1),
  artikelnummer: str,
  bezeichnung: str,
  einheit: str,
  einheit_de: str,
  einheit_en: str,
  einkaufspreis: num,
  verkaufspreis: num,
  beschreibung_de: str,
  beschreibung_en: str,
  spezifikation_labels_de: str,
  spezifikation_werte_de: str,
  spezifikation_labels_en: str,
  spezifikation_werte_en: str,
});
export type Base44ArticleRecord = z.infer<typeof base44ArticleRecordSchema>;

export const base44OfferRecordSchema = z.object({
  kalkulation_id: z.string().min(1),
  status: str,
  language: str,
  customer: str,
  date: str,
  agb_validity: str,
});
export type Base44OfferRecord = z.infer<typeof base44OfferRecordSchema>;

/** Ein Häppchen des Imports: einige Kalkulationen samt Positionen, Angebot und benutzten Artikeln. */
export const quoteCalcBase44CalculationImportSchema = z.object({
  calculations: z
    .array(
      z.object({
        calculation: base44CalculationRecordSchema,
        positions: z.array(base44PositionRecordSchema).max(400),
        offer: base44OfferRecordSchema.nullable(),
      }),
    )
    .min(1)
    .max(25),
  articles: z.array(base44ArticleRecordSchema).max(2000),
  /** Schon übernommene Kalkulationen erneut schreiben (sonst überspringen). */
  overwrite: z.boolean().default(false),
});
export type QuoteCalcBase44CalculationImport = z.infer<typeof quoteCalcBase44CalculationImportSchema>;

export const quoteCalcBase44CalculationImportResultSchema = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.array(z.object({ number: z.string(), reason: z.string() })),
  warnings: z.array(z.string()),
});
export type QuoteCalcBase44CalculationImportResult = z.infer<typeof quoteCalcBase44CalculationImportResultSchema>;

/** base44-Name der Gruppe in den Feldern der Kalkulation (`kunststoff_prozent` …). */
const FIELD_PREFIX: Record<QuoteCalcGroupKey, string> = {
  PLASTIC: 'kunststoff',
  ELECTRICAL: 'elektro',
  STEEL: 'stahl',
  ASSEMBLY: 'montage',
  DOCUMENTATION: 'doku',
  FREIGHT: 'fracht',
  OPTIONS: 'optionen',
};

const STATUS: Record<string, QuoteCalcStatus> = {
  draft: 'DRAFT',
  sent: 'SENT',
  followup: 'SENT',
  won: 'WON',
  lost: 'LOST',
};

const UNIT_LABELS: Record<string, string> = { pc: 'Stk', pcs: 'Stk', 'pcs.': 'Stk', ls: 'pauschal', psch: 'pauschal', set: 'Satz', m: 'm', h: 'h' };

/** Euro (Float aus base44) → Mikro-Einheiten als String. */
export function eurosToMicros(value: number | null | undefined): string {
  return String(Math.round((value ?? 0) * 1_000_000));
}

const differs = (a: number | null | undefined, b: number | null | undefined, epsilon = 1e-6) =>
  Math.abs((a ?? 0) - (b ?? 0)) > epsilon;

/** „26011930-Rev.1", „26011930 - Rev. 2" → Nummer + Revision; alles andere bleibt Nummer mit Revision 0. */
export function parseBase44Number(raw: string): { number: string; revision: number } {
  const match = /^(.+?)\s*-\s*Rev\.?\s*(\d+)$/i.exec(raw.trim());
  if (match) return { number: match[1]!.trim(), revision: Number(match[2]) };
  return { number: raw.trim(), revision: 0 };
}

/** Zeilenweise Merkmale („Typ\nWerkstoff" + „NCS720S\nPOM") → Paare; leere Paare fallen weg. */
export function parseBase44Specs(labels: string | null | undefined, values: string | null | undefined): { label: string; value: string }[] {
  const left = (labels ?? '').split('\n');
  const right = (values ?? '').split('\n');
  return left
    .map((label, index) => ({ label: label.trim(), value: (right[index] ?? '').trim() }))
    .filter((pair) => pair.label || pair.value);
}

function isoDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(raw.trim()) ? new Date(`${raw.trim()}T00:00:00.000Z`) : new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export interface MappedBase44Calculation {
  legacyId: string;
  number: string;
  revision: number;
  status: QuoteCalcStatus;
  name: string;
  subtitle: string | null;
  customerName: string | null;
  language: 'DE' | 'EN';
  scraperType: ScraperType;
  basinCount: number;
  lengthM: number;
  widthM: number;
  heightM: number;
  waterLevelM: number;
  flightSpacingM: number;
  scrapingSpeedCmS: number;
  flightCountOverride: number | null;
  flightLengthOverrideM: number | null;
  materialOverheadPct: number;
  salesOverheadPct: number;
  commissionPct: number;
  finalPriceMicros: string | null;
  validUntil: string | null;
  sentAt: string | null;
  createdAt: string | null;
  groups: QuoteCalcGroup[];
  /** Hinweise für den Importbericht (z. B. unbekannter Räumertyp). */
  warnings: string[];
}

export function mapBase44Calculation(
  input: { calculation: Base44CalculationRecord; positions: Base44PositionRecord[]; offer: Base44OfferRecord | null },
  context: {
    articlesById: ReadonlyMap<string, Base44ArticleRecord>;
    /** ProERP-Artikel-ID je Artikelnummer (nur Treffer). */
    articleIdByNumber: ReadonlyMap<string, string>;
    formulas: readonly QuoteCalcFormulaRow[];
  },
): MappedBase44Calculation {
  const { calculation: calc, offer } = input;
  const warnings: string[] = [];
  const label = calc.kalkulation_id;

  let scraperType = base44ScraperType(calc.type);
  if (!scraperType) {
    warnings.push(`${label}: unbekannter Räumertyp „${calc.type ?? ''}" — als Sonderanlage übernommen`);
    scraperType = 'OTHER';
  }

  const dimensions = {
    basinCount: Math.max(1, Math.round(calc.beckenanzahl ?? 1)),
    lengthM: calc.laenge ?? 0,
    widthM: calc.breite ?? 0,
    heightM: calc.hoehe ?? 0,
    waterLevelM: calc.wasserspiegel ?? 0,
    flightSpacingM: calc.balkenabstand ?? 0,
  };
  const formulas = formulasForScraperType(context.formulas, scraperType);
  const derived = deriveFormulaVariables({ dimensions, formulas });
  const storedFlights = calc.anzahl_balken == null ? null : Math.floor(calc.anzahl_balken + 0.5 + 1e-9);
  const flightCountOverride = storedFlights !== null && storedFlights !== derived.flightCount ? storedFlights : null;
  const flightLengthOverrideM =
    calc.balkenlaenge != null && differs(calc.balkenlaenge, derived.flightLengthM) ? calc.balkenlaenge : null;

  const record = calc as unknown as Record<string, unknown>;
  const groups: QuoteCalcGroup[] = QUOTE_CALC_GROUP_KEYS.map((key) => {
    const defaults = QUOTE_CALC_GROUP_DEFAULTS[key];
    const prefix = FIELD_PREFIX[key];
    const mode = record[`${prefix}_calc_mode`];
    const pricingMode = mode === 'progressive' ? 'PROGRESSIVE' : mode === 'retrograd' ? 'RETROGRADE' : defaults.pricingMode;
    const surcharge = record[`${prefix}_prozent`];
    const split = record[`${prefix}_kosten_aufteilen`];
    const lines = input.positions
      .filter((position) => base44GroupKey(position.gruppe) === key)
      .sort((a, b) => (a.reihenfolge ?? 0) - (b.reihenfolge ?? 0))
      .map((position): QuoteCalcLine => {
        const article = position.artikel_id ? context.articlesById.get(position.artikel_id) : undefined;
        const number = article?.artikelnummer?.trim() || null;
        const unitRaw = (article?.einheit ?? '').trim();
        const specsDe = parseBase44Specs(article?.spezifikation_labels_de, article?.spezifikation_werte_de);
        const specsEn = parseBase44Specs(article?.spezifikation_labels_en, article?.spezifikation_werte_en);
        const hasOfferText = Boolean(article?.beschreibung_de?.trim() || article?.beschreibung_en?.trim());
        const isRetro = pricingMode === 'RETROGRADE';
        return {
          id: position.id,
          category: position.kategorie.trim() || 'Position',
          articleId: number ? (context.articleIdByNumber.get(number) ?? null) : null,
          articleNumber: number,
          articleName: (article?.bezeichnung ?? '').trim(),
          unit: article?.einheit_de?.trim() || UNIT_LABELS[unitRaw.toLowerCase()] || unitRaw || 'Stk',
          purchasePriceMicros: eurosToMicros(position.einzel_ek),
          isPurchasePriceOverridden: article ? differs(position.einzel_ek, article.einkaufspreis) : false,
          listPriceMicros: eurosToMicros(article ? article.verkaufspreis : position.einzel_vk),
          quantityOverride: null,
          // Retrograd zählt der VK: weicht er vom Artikel ab, war er von Hand gesetzt.
          unitPriceOverrideMicros: isRetro && article && differs(position.einzel_vk, article.verkaufspreis) ? eurosToMicros(position.einzel_vk) : null,
          offerTotalOverrideMicros: position.vk_angebot_override == null ? null : eurosToMicros(position.vk_angebot_override),
          isPerBasin: true,
          showInOffer: position.show_in_offer ?? false,
          showInSpecs: position.show_in_specs ?? false,
          offerText: hasOfferText ? { de: article?.beschreibung_de?.trim() ?? '', en: article?.beschreibung_en?.trim() ?? '' } : null,
          specs: specsDe.length || specsEn.length ? { de: specsDe, en: specsEn } : null,
        };
      });
    return {
      key,
      title: defaults.title,
      pricingMode,
      surchargePct: typeof surcharge === 'number' ? surcharge : defaults.surchargePct,
      isSplit: typeof split === 'boolean' ? split : defaults.isSplit,
      lines,
    };
  });

  // Mengen: was die Formel nicht von selbst ergibt (oder gesperrt war), wird „von Hand".
  const header = {
    ...dimensions,
    flightCountOverride,
    flightLengthOverrideM,
    materialOverheadPct: calc.materialgemeinkosten_prozent ?? 12.5,
    salesOverheadPct: calc.vetriebsgemeinkosten_prozent ?? 35,
    commissionPct: calc.verkaeufer_provision_prozent ?? 0,
    finalPriceMicros: calc.finaler_verkaufspreis == null ? null : eurosToMicros(calc.finaler_verkaufspreis),
  };
  const result = computeQuoteCalculation(toQuoteCalcInput({ ...header, groups, scraperType, subtitle: null, scrapingSpeedCmS: calc.raeumgeschwindigkeit ?? 1 }, () => formulas));
  const autoById = new Map(result.groups.flatMap((group) => group.lines.map((line) => [line.id, line.autoQuantity])));
  const stored = new Map(input.positions.map((position) => [position.id, position]));
  for (const group of groups) {
    for (const line of group.lines) {
      const position = stored.get(line.id)!;
      const quantity = position.menge ?? 0;
      if (position.menge_locked || differs(autoById.get(line.id), quantity)) line.quantityOverride = quantity;
    }
  }
  const unknownGroups = input.positions.filter((position) => !base44GroupKey(position.gruppe));
  if (unknownGroups.length) warnings.push(`${label}: ${unknownGroups.length} Position(en) mit unbekannter Gruppe nicht übernommen`);

  const { number, revision } = parseBase44Number(calc.kalkulation_id);
  const status = STATUS[(offer?.status ?? '').toLowerCase()] ?? 'DRAFT';
  const language = (offer?.language ?? '').toLowerCase() === 'en' ? 'EN' : 'DE';
  return {
    legacyId: calc.id,
    number,
    revision,
    status,
    name: calc.projektname?.trim() || calc.kalkulation_id,
    subtitle: calc.angebotsuntertitel?.trim() || null,
    customerName: calc.kunde?.trim() || offer?.customer?.trim() || null,
    language,
    scraperType,
    ...header,
    scrapingSpeedCmS: calc.raeumgeschwindigkeit ?? 1,
    validUntil: isoDate(offer?.agb_validity),
    sentAt: status === 'DRAFT' ? null : isoDate(offer?.date ?? calc.datum),
    createdAt: isoDate(calc.created_date) ?? isoDate(calc.datum),
    groups,
    warnings,
  };
}
